import React, { useContext, useEffect, useState } from 'react';
import { Typography, Divider, IconButton, CircularProgress, TableContainer, Table, TableHead, TableBody, Paper, TablePagination, Dialog, DialogContent, DialogTitle, Button, TableRow, TableCell, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { ContextSession } from "../../../services/ContextSession";
import { useMediaQuery } from '@mui/material';
import { tableCellClasses } from '@mui/material/TableCell';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: '#19438C',
        color: 'white',
        fontSize: 16,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        borderRight: 'none',
        borderLeft: 'none',
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
    '&:hover': {
        backgroundColor: '#C1C1C1',
    },
}));

function SolicitudCambioDatos() {
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    let sessi = useContext(ContextSession);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [searchTerm, setSearchTerm] = useState('');
    const [filas, setFilas] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [estadoFiltro, setEstadoFiltro] = useState('PENDIENTE'); // Estado para el filtro de estado
    const [solicitudData, setSolicitudData] = useState(null); // Para almacenar los datos de la solicitud detallada
    const [loadingSolicitud, setLoadingSolicitud] = useState(false); // Para saber si estamos cargando los datos de la solicitud


    // Estado de confirmación
    const [confirmationAction, setConfirmationAction] = useState(null);  // Acción de confirmación (Aceptar/Rechazar)
    const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false); // Estado para el diálogo de confirmación
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false); // Diálogo de confirmación de resultado

    // Este useEffect restablecerá la página a 0 cuando se cambien los filtros
    useEffect(() => {
        setPage(0); // Resetear a la primera página cuando el filtro cambia
    }, [estadoFiltro, searchTerm]); // Dependencias del filtro


    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const navigate = useNavigate();

    // Fetch data from API
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch('https://be.injupemp.gob.hn:8443/api/SolicitudCambioCorreo/');
                const data = await response.json();
                setFilas(data);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching data: ", error);
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Filter rows based on search term and selected state filter
    // Filtrar filas basado en el término de búsqueda y el filtro de estado
    const filteredRows = filas
        .filter((row) => {
            // Filtrado por búsqueda
            const matchesSearch =
                (searchTerm === '' ||
                    row.dni.toString().includes(searchTerm) ||
                    row.nombre.toLowerCase().includes(searchTerm) ||
                    row.telefono.includes(searchTerm) ||
                    row.correoNuevo.toLowerCase().includes(searchTerm));

            // Filtrado por estado
            const matchesEstado = estadoFiltro === 'Todas' || row.estadoVerificado === estadoFiltro;

            return matchesSearch && matchesEstado;
        })
        .sort((a, b) => {
            // Si el estado de la fila es "PENDIENTE", ponerla antes que otras
            if (estadoFiltro === 'Todas') {
                if (a.estadoVerificado === 'PENDIENTE' && b.estadoVerificado !== 'PENDIENTE') {
                    return -1; // A es antes que B
                }
                if (b.estadoVerificado === 'PENDIENTE' && a.estadoVerificado !== 'PENDIENTE') {
                    return 1; // B es antes que A
                }
            }
            return 0; // Si no son "PENDIENTE", no cambiar el orden
        });


    const handleOpenDialog = async (row) => {
        setSelectedRow(row);
        setLoadingSolicitud(true); // Indicamos que estamos cargando los datos de la solicitud
        try {
            const formData = new FormData();
            formData.append('ID', row.ID); // Enviamos el ID de la solicitud seleccionada
            formData.append('apikey', "SW5qdVBDb24yMDIzIw==")

            const response = await fetch('https://be.injupemp.gob.hn:8443/api/solicitud/correo/', {
                method: 'POST',
                body: formData
            });
            const data = await response.json();
            if (data.Solicitud) {
                setSolicitudData(data); // Guardamos los datos de la solicitud detallada
            } else {
                console.error('No se pudo obtener la solicitud');
            }
        } catch (error) {
            console.error("Error fetching solicitud details: ", error);
        } finally {
            setLoadingSolicitud(false); // Terminamos de cargar
        }

        setOpenDialog(true); // Abrimos el Dialog
    };


    // Cerrar el Dialog
    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedRow(null);
        setConfirmationAction(null);
    };

    const handleSendRequest = async () => {
        if (selectedRow && confirmationAction) {
            const idSolicitud = selectedRow.ID;
            const estadoVerificado = confirmationAction === 'Aceptar' ? 'ACEPTADO' : 'RECHAZADO';
            const fechaVerificado = new Date().toISOString(); // Fecha actual en formato ISO

            // Crear el FormData
            const formData = new FormData();
            formData.append('estadoVerificado', estadoVerificado);
            formData.append('fechaVerificado', fechaVerificado);
            formData.append('IdUsuarioVerificado', sessi.sessionData.id)

            try {
                const response = await fetch(`https://be.injupemp.gob.hn:8443/api/SolicitudCambioCorreo/${idSolicitud}/`, {
                    method: 'PATCH',
                    body: formData, // Enviar como FormData
                });

                if (response.ok) {
                    console.log('Solicitud actualizada correctamente');

                    // Actualizamos el estado de las filas después de la respuesta de la API
                    setFilas((prevFilas) =>
                        prevFilas.map((fila) =>
                            fila.ID === idSolicitud ? { ...fila, estadoVerificado } : fila
                        )
                    );

                    setOpenDialog(false);
                    setOpenConfirmDialog(true);
                } else {
                    console.error('Error al enviar la solicitud');
                    alert('Hubo un error al enviar la solicitud');
                }
            } catch (error) {
                console.error("Error al enviar la solicitud: ", error);
                alert('Hubo un error al enviar la solicitud');
            }
        }
    };


    const handleDialogAction = (action) => {
        setConfirmationAction(action);  // Asignamos la acción seleccionada
        setConfirmationDialogOpen(true);  // Abrimos el diálogo de confirmación
    };

    const handleConfirmation = (confirm) => {
        if (confirm) {
            handleSendRequest();  // Si el usuario confirma, enviamos la solicitud
        }
        setConfirmationDialogOpen(false);  // Cerramos el diálogo de confirmación
    };

    return (
        <div className="container" style={{ marginTop: 10 }}>
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                    <CircularProgress />
                </div>
            ) : (
                <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <h2><b>Lista de Solicitudes de Actualización De Datos</b></h2>
                    </div>

                    {/* Filtro de estado */}
                    <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
                        <FormControl style={{ minWidth: 200 }}>
                            <InputLabel>Filtrar por estado</InputLabel>
                            <Select
                                value={estadoFiltro}
                                onChange={(e) => setEstadoFiltro(e.target.value)}
                                label="Filtrar por estado"
                            >
                                <MenuItem value="Todas">Todas</MenuItem>
                                <MenuItem value="ACEPTADO">ACEPTADO</MenuItem>
                                <MenuItem value="RECHAZADO">RECHAZADO</MenuItem>
                                <MenuItem value="PENDIENTE">PENDIENTE</MenuItem>
                            </Select>
                        </FormControl>
                    </div>

                    <div className="row" style={{ marginTop: '20px' }}>
                        <TableContainer component={Paper}>
                            <Table style={{ fontSize: isMobile ? '7px' : '14px' }}>
                                <TableHead>
                                    <StyledTableRow>
                                        <StyledTableCell style={{ textAlign: 'center' }}><strong>ID. Solicitud</strong></StyledTableCell>
                                        <StyledTableCell style={{ textAlign: 'center' }}><strong>Nombre del Solicitante</strong></StyledTableCell>
                                        <StyledTableCell style={{ textAlign: 'center' }}><strong>Número de Identidad</strong></StyledTableCell>
                                        <StyledTableCell style={{ textAlign: 'center' }}><strong>Teléfono</strong></StyledTableCell>
                                        <StyledTableCell style={{ textAlign: 'center' }}><strong>Correo Nuevo</strong></StyledTableCell>
                                        <StyledTableCell style={{ textAlign: 'center' }}><strong>Fecha Solicitud</strong></StyledTableCell>
                                        <StyledTableCell style={{ textAlign: 'center' }}><strong>Estado</strong></StyledTableCell>
                                        <StyledTableCell style={{ textAlign: 'center' }}><strong>Acciones</strong></StyledTableCell>
                                    </StyledTableRow>
                                </TableHead>

                                <TableBody>
                                    {filteredRows.length > 0 ? (
                                        filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                                            <StyledTableRow key={row.ID}>
                                                <StyledTableCell style={{ textAlign: 'center' }}>{row.ID}</StyledTableCell>
                                                <StyledTableCell style={{ textAlign: 'center' }}>{row.nombre}</StyledTableCell>
                                                <StyledTableCell style={{ textAlign: 'center' }}>{row.dni}</StyledTableCell>
                                                <StyledTableCell style={{ textAlign: 'center' }}>{row.telefono}</StyledTableCell>
                                                <StyledTableCell style={{ textAlign: 'center' }}>{row.correoNuevo}</StyledTableCell>
                                                <StyledTableCell style={{ textAlign: 'center' }}>{new Date(row.fechaSolicitud).toLocaleString()}</StyledTableCell>
                                                <StyledTableCell style={{ textAlign: 'center' }}>{row.estadoVerificado}</StyledTableCell>
                                                <StyledTableCell
                                                    style={{
                                                        textAlign: 'center', // Asegura que el contenido esté centrado horizontalmente
                                                        alignItems: 'center', // Centra verticalmente el contenido
                                                        justifyContent: 'center', // Centra horizontalmente el contenido
                                                    }}
                                                >
                                                    <Button
                                                        onClick={() => handleOpenDialog(row)} // Llamamos a handleOpenDialog con la fila seleccionada
                                                        disabled={row.estadoVerificado !== "PENDIENTE" || loadingSolicitud} // Deshabilitar si ya se está cargando
                                                        variant="contained"
                                                        color="primary"
                                                        style={{
                                                            alignItems: 'center', // Asegura que el texto (y el spinner) estén alineados verticalmente
                                                            justifyContent: 'center', // Centra el contenido del botón
                                                            width: '180px', // Ajusta el ancho del botón si es necesario
                                                        }}
                                                    >
                                                        {selectedRow && selectedRow.ID === row.ID && loadingSolicitud ? (
                                                            <CircularProgress size={24} style={{ color: 'white' }} />
                                                        ) : (
                                                            'Revisar Solicitud'
                                                        )}
                                                    </Button>
                                                </StyledTableCell>

                                            </StyledTableRow>
                                        ))
                                    ) : (
                                        <StyledTableRow>
                                            <StyledTableCell colSpan={8} style={{ textAlign: 'center' }}>
                                                No se encontraron datos
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    )}
                                </TableBody>

                            </Table>
                        </TableContainer>

                        {filteredRows.length > 0 && (
                            <TablePagination
                                rowsPerPageOptions={isMobile ? [3, 5, 10] : [5, 10, 20]}
                                component="div"
                                count={filteredRows.length} // Usamos las filas filtradas aquí
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                labelRowsPerPage={isMobile ? "Filas:" : "Filas por página"}
                                labelDisplayedRows={({ from, to, count }) => `${from} - ${to} de ${count}`}
                            />

                        )}
                    </div>

                    <Dialog open={openDialog} onClose={handleCloseDialog}>
                        <DialogTitle style={{ position: 'relative', textAlign: 'center' }}>
                            Revisar Solicitud
                            <IconButton
                                onClick={handleCloseDialog}
                                style={{
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px',
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent>
                            {loadingSolicitud ? (
                                <CircularProgress /> // Muestra un cargador mientras obtenemos los datos
                            ) : (
                                solicitudData && selectedRow && (
                                    <>
                                        {/* Detalles de la solicitud */}
                                        <div><strong>Nombre del Solicitante:</strong> {solicitudData.Solicitud.nombre}</div>
                                        <div><strong>Número de Identidad:</strong> {solicitudData.Solicitud.dni}</div>
                                        <div><strong>Teléfono:</strong> {solicitudData.Solicitud.telefono}</div>
                                        <div><strong>Correo Nuevo:</strong> {solicitudData.Solicitud.correoNuevo}</div>
                                        <div><strong>Fecha Solicitud:</strong> {new Date(solicitudData.Solicitud.fechaSolicitud).toLocaleString()}</div>
                                        <div><strong>Estado Verificado:</strong> {solicitudData.Solicitud.estadoVerificado}</div>

                                        {/* Mostrar las imágenes en base64 */}
                                        <div>
                                            {/* Imagen 1 */}
                                            {solicitudData.foto_1 && (
                                                <>
                                                    <Typography variant="h6" align="center">Identidad Frente</Typography>
                                                    <img
                                                        src={`data:image/png;base64,${solicitudData.foto_1}`}
                                                        alt="Foto 1"
                                                        style={{ width: '100%', height: 'auto', margin: '10px 0' }}
                                                    />
                                                    <Divider style={{ margin: '10px 0' }} />
                                                </>
                                            )}

                                            {/* Imagen 2 */}
                                            {solicitudData.foto_2 && (
                                                <>
                                                    <Typography variant="h6" align="center">Identidad Reverso</Typography>
                                                    <img
                                                        src={`data:image/png;base64,${solicitudData.foto_2}`}
                                                        alt="Foto 2"
                                                        style={{ width: '100%', height: 'auto', margin: '10px 0' }}
                                                    />
                                                    <Divider style={{ margin: '10px 0' }} />
                                                </>
                                            )}

                                            {/* Imagen 3 */}
                                            {solicitudData.foto_3 && (
                                                <>
                                                    <Typography variant="h6" align="center">Foto con Identidad</Typography>
                                                    <img
                                                        src={`data:image/png;base64,${solicitudData.foto_3}`}
                                                        alt="Foto 3"
                                                        style={{ width: '100%', height: 'auto', margin: '10px 0' }}
                                                    />
                                                </>
                                            )}
                                        </div>

                                        {/* Botones de acción */}
                                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                                            <Button
                                                onClick={() => handleDialogAction('Aceptar')}
                                                variant="contained"
                                                color="success"
                                                style={{ marginRight: '10px', display: 'flex', alignItems: 'center' }}
                                            >
                                                Aceptar Solicitud
                                                <CheckCircleIcon style={{ marginLeft: '8px' }} />
                                            </Button>
                                            <Button
                                                onClick={() => handleDialogAction('Rechazar')}
                                                variant="contained"
                                                color="error"
                                                style={{ display: 'flex', alignItems: 'center' }}
                                            >
                                                Rechazar Solicitud
                                                <CancelIcon style={{ marginLeft: '8px' }} />
                                            </Button>
                                        </div>
                                    </>
                                )
                            )}
                        </DialogContent>
                    </Dialog>


                    {/* Dialog de Confirmación */}
                    <Dialog open={confirmationDialogOpen} onClose={() => setConfirmationDialogOpen(false)}>
                        <DialogTitle textAlign={'center'}>Confirmación</DialogTitle>
                        <DialogContent>
                            <div style={{ textAlign: 'justify' }}>
                                ¿Está seguro de <b>{confirmationAction === 'Aceptar' ? 'Aceptar' : 'Rechazar'}</b> la solicitud de actualización de correo del participante {selectedRow?.nombre}?
                            </div>
                            <div style={{ marginTop: '20px', textAlign: 'center' }}>
                                <Button
                                    onClick={() => handleConfirmation(true)}
                                    variant="contained"
                                    color="success"
                                    style={{ marginRight: '10px' }}
                                >
                                    Sí
                                </Button>
                                <Button
                                    onClick={() => handleConfirmation(false)}
                                    variant="contained"
                                    color="error"
                                >
                                    No
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>

                    {/* Dialog de Confirmación de Resultado */}
                    <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
                        <DialogTitle textAlign={'center'}>Confirmación</DialogTitle>
                        <DialogContent>
                            {confirmationAction === 'Aceptar' ? (
                                <div>La solicitud de actualización de correo ha sido aceptada correctamente.</div>
                            ) : (
                                <div>La solicitud de actualización de correo ha sido rechazada correctamente.</div>
                            )}
                            <div style={{ marginTop: '20px', textAlign: 'center' }}>
                                <Button
                                    onClick={() => setOpenConfirmDialog(false)}
                                    variant="contained"
                                    color="primary"
                                >
                                    Cerrar
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                </>
            )}
        </div>
    );
}

export default SolicitudCambioDatos;
